import {
  ArrowRotateLeftIcon,
  AreaViewIcon,
  ListIcon,
  makeStyles,
  SurfaceViewIcon,
  Typography,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import {
  getObjectLabels,
  getWorkOrderHiddenItems,
  getWorkOrderLayoutKey,
  restoreWorkOrderCard,
  setLineItemHiddenOnWorkOrder
} from '@paintscout/util/builder'
import type { RenderableItem, WorkOrderLayoutView, WorkOrderViewItem } from '@paintscout/util/builder'
import { localizeDecimals } from '@paintscout/util/builder/util/format-number/format-number'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import React from 'react'
import { useQuote } from '../context'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        border: `1px solid ${neutral.P85}`,
        // `xl` (12px) — closest token to the mock's 10px panel, same as the cards.
        borderRadius: theme.borderRadius.xl,
        overflow: 'hidden'
      },
      // Header bar naming the section. The nav bar's "Hidden items" button controls whether this is on
      // screen at all, so there's no collapse control here.
      header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minHeight: 44,
        padding: theme.spacing(1, 2),
        backgroundColor: neutral.P94,
        color: neutral.P35,
        fontSize: theme.typography.pxToRem(15),
        fontWeight: 500
      },
      headerIcon: {
        fontSize: 18,
        color: neutral.P60
      },
      body: {
        backgroundColor: neutral.White,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2)
      },
      // One block per view: its label, then its cards. No rules between rows — the gaps carry it.
      group: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2)
      },
      groupLabelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
      },
      groupIcon: {
        fontSize: 18,
        color: neutral.P60
      },
      groupLabel: {
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(16)
      },
      row: {
        display: 'flex',
        // Baseline keeps Restore level with the name's first line when a long name wraps.
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: theme.spacing(2)
      },
      // Wraps rather than ellipsizing, so the added-work label after the name stays visible on mobile.
      name: {
        color: neutral.P35,
        fontSize: theme.typography.pxToRem(16),
        minWidth: 0,
        overflowWrap: 'anywhere'
      },
      // Which added-work group a hidden card came from, so it can be told from a bid card of the same name.
      sectionLabel: {
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14)
      },
      // Baseline here (with the icon opted out below) takes the button's baseline from the "Restore"
      // text rather than the icon's bottom edge, so it lines up with the name in the row.
      restoreButton: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(0.5),
        border: 'none',
        background: 'none',
        padding: 0,
        font: 'inherit',
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50,
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'opacity 120ms ease',
        '&:hover': { opacity: 0.75 }
      },
      restoreIcon: {
        fontSize: 16,
        alignSelf: 'center'
      }
    }
  },
  { name: 'WorkOrderHiddenItemsNext' }
)

/**
 * The panel's groups: one per card view, plus the view-independent line items hidden by the
 * estimate's "Hidden on Work Order" checkbox.
 */
type HiddenGroupKey = WorkOrderLayoutView | 'line_item'

/** Resolve an item name that may be a plain string or an overridable {useCustom,custom,default}. */
function resolveItemName(item: RenderableItem): string {
  const { name } = item
  if (!name) {
    return ''
  }
  if (typeof name === 'string') {
    return name
  }
  return (name.useCustom ? name.custom : name.default) ?? ''
}

/**
 * The "Hidden items (N)" section for the Work Order view: every hidden card across both views,
 * grouped under Areas / Surfaces, plus the line items the estimate's "Hidden on Work Order" checkbox
 * hides, each with a Restore action. The view renders it above the cards while the nav bar's Hidden
 * items button is toggled on. Null when nothing is hidden.
 */
export default function WorkOrderHiddenItemsNext() {
  const classes = useStyles()
  const { quote, updateQuote } = useQuote()
  const { options } = useClientOptions()
  const { __, lang } = useTranslation()
  const objectLabels = getObjectLabels({ options })

  const hidden = getWorkOrderHiddenItems({ quote, options, lang })
  if (hidden.total === 0) {
    return null
  }

  // A view group's card is un-hidden on that view's layout, under its section's layout key; a line
  // item's hide is the estimate's "Hidden on Work Order" checkbox, so restoring it clears that
  // checkbox instead (ENG-1930).
  const handleRestore = (group: HiddenGroupKey, item: WorkOrderViewItem) => {
    updateQuote({
      quote:
        group === 'line_item'
          ? setLineItemHiddenOnWorkOrder({ quote, key: item.key, hidden: false })
          : restoreWorkOrderCard({
              quote,
              view: group,
              key: getWorkOrderLayoutKey(item.workOrderSection, item.key)
            })
    })
  }

  const sectionLabels: Partial<Record<WorkOrderViewItem['workOrderSection'], string>> = {
    additional: __(objectLabels.additionalWork.value),
    'added-options': __('Options Added')
  }

  const groups: {
    group: HiddenGroupKey
    label: string
    Icon: React.ComponentType<{ className?: string }>
    items: WorkOrderViewItem[]
  }[] = [
    { group: 'area', label: __('Areas'), Icon: AreaViewIcon, items: hidden.area },
    { group: 'substrate', label: __(objectLabels.substrate.plural), Icon: SurfaceViewIcon, items: hidden.substrate },
    // Line items hidden by the estimate's checkbox are hidden on both views, so they get their own
    // group rather than being repeated under Areas and Surfaces.
    { group: 'line_item', label: __('Line Items'), Icon: ListIcon, items: hidden.lineItem }
  ]

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <VisibilityOffIcon className={classes.headerIcon} />
        <span>{`${__('Hidden items')} (${hidden.total})`}</span>
      </div>
      <div className={classes.body}>
        {groups
          .filter((group) => group.items.length > 0)
          .map((group) => (
            <div className={classes.group} key={group.group}>
              <div className={classes.groupLabelRow}>
                <group.Icon className={classes.groupIcon} />
                <Typography className={classes.groupLabel}>{group.label}</Typography>
              </div>
              {group.items.map((item) => (
                <div
                  className={classes.row}
                  key={`${group.group}:${getWorkOrderLayoutKey(item.workOrderSection, item.key)}`}
                >
                  {/* Name + dimensions, matching how the card itself is titled. */}
                  <Typography className={classes.name}>
                    {item.subtitle
                      ? `${resolveItemName(item)} (${localizeDecimals(item.subtitle, lang)})`
                      : resolveItemName(item)}
                    {sectionLabels[item.workOrderSection] && (
                      <span className={classes.sectionLabel}>{` · ${sectionLabels[item.workOrderSection]}`}</span>
                    )}
                  </Typography>
                  <button
                    type="button"
                    className={classes.restoreButton}
                    onClick={() => handleRestore(group.group, item)}
                  >
                    <ArrowRotateLeftIcon className={classes.restoreIcon} />
                    {__('Restore')}
                  </button>
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  )
}
